'use client'

import { tagCMSBlock } from '@/helpers/layout'
import { cx } from 'cva'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Corridor } from '../atoms/Corridor'
import type { LinkTabType } from '../atoms/LinkTab'
import LinkTabHeader from '../molecules/LinkTabHeader'

export type InPageNavigationBlockProps = {
	tabs: LinkTabType[]
	className?: string
}

const STICKY_HEADER_CONTENT_OFFSET = 48

export const InPageNavigationBlock: FC<InPageNavigationBlockProps> = ({ tabs, className }) => {
	const stickyHeaderRef = useRef<HTMLDivElement>(null)
	const [isSticky, setIsSticky] = useState(false)
	const [scrollOffset, setScrollOffset] = useState(0)

	// Check if sticky header is stuck
	useEffect(() => {
		const stickyHeader = stickyHeaderRef.current
		if (!stickyHeader) return

		const checkSticky = () => {
			// When sticky, the element's top will be at 0
			const rect = stickyHeader.getBoundingClientRect()
			setIsSticky(rect.top <= 1)
		}

		window.addEventListener('scroll', checkSticky, { passive: true })

		// Initial check
		checkSticky()

		return () => {
			window.removeEventListener('scroll', checkSticky)
		}
	}, [])

	// Update scroll offset when sticky header changes
	useEffect(() => {
		const stickyHeader = stickyHeaderRef.current
		if (!stickyHeader) return

		const updateScrollOffset = () => {
			setScrollOffset(stickyHeader.offsetHeight + STICKY_HEADER_CONTENT_OFFSET)
		}

		updateScrollOffset()

		const resizeObserver = new ResizeObserver(updateScrollOffset)
		resizeObserver.observe(stickyHeader)

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	return (
		<section
			ref={stickyHeaderRef}
			className={cx(
				tagCMSBlock(['pre-header', 'sticky']),
				'overflow-hidden sticky top-0 z-50 bg-white transition-shadow duration-200',
				isSticky && 'shadow-nav',
				className,
			)}
		>
			<Corridor>
				<LinkTabHeader
					tabs={tabs}
					prefetch={false}
					isSticky={isSticky}
					scrollOffset={scrollOffset}
				/>
			</Corridor>
		</section>
	)
}

export default InPageNavigationBlock
